<script setup lang="ts">
const props = defineProps({
  mode: {
    type: String,
    default: 'button',
    validator: (value: string) => ['button', 'link'].includes(value),
  },
  type: {
    type: String,
    default: 'primary',
    validator: (value: string) =>
      ['primary', 'secondary', 'tertiary', 'white'].includes(value),
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value: string) => ['large', 'medium'].includes(value),
  },
  sizeIco: {
    type: String,
    validator: (value: string) => ['16', '20', '24', '28'].includes(value),
  },
  responsiveIco: {
    type: Boolean,
    default: false,
  },
  iconBefore: {
    type: String,
    default: '',
  },
  iconAfter: {
    type: String,
    default: '',
  },
  fontClass: {
    type: String,
    default: 'body-m-regular',
    validator: (value: string) =>
      [
        'body-l-medium',
        'body-m-medium',
        'body-l-regular',
        'body-m-regular',
      ].includes(value),
  },
  minWidth: {
    type: Number,
    default: 0,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  resize: {
    type: Boolean,
    default: false,
  },
  path: {
    type: [String, Object],
  },
  fill: {
    type: String,
    default: '',
  },
  stroke: {
    type: String,
    default: '',
  },
  hoverFill: {
    type: String,
    default: '',
  },
  hoverStroke: {
    type: String,
    default: '',
  },
});
const touched = ref(false);
const emit = defineEmits(['click']);
const clickButtons = (e: Event): void => {
  if (!props.disabled) {
    emit('click', e);
  }
};

const iconColor = computed(() => {
  switch (props.type) {
    case 'primary':
      return '#363EF8';
    case 'secondary':
      return '#212121';
    case 'tertiary':
      return '#7B7D84';
    case 'white':
      return '#FFFFFF';
  }
});
</script>

<template>
  <nuxt-link
    v-if="mode === 'link'"
    :to="path"
    v-bind="$attrs"
    :class="[
      'button-wrapper-link',
      type,
      `${type}__${size}`,
      { touched: touched },
      { button__disabled: disabled },
    ]"
    :style="[
      Number(minWidth) > 0 ? `minWidth: ${minWidth}px` : '',
      resize ? `width: 100% ` : '',
    ]"
    @click="clickButtons"
    @touchstart="touched = true"
  >
    <div v-if="!!iconBefore" class="button__ico-before">
      <UiIcon
        :name="iconBefore"
        :size="sizeIco"
        :response-size="responsiveIco ? [16, 16, 16, 20, 20, 20] : []"
        :fill="fill"
        :stroke="stroke || iconColor"
        :hover-fill="hoverFill"
        :hover-stroke="hoverStroke"
      />
    </div>
    <div :class="['button-text', fontClass]">
      <slot />
    </div>
    <div v-if="!!iconAfter" class="button__ico-after">
      <UiIcon
        :name="iconAfter"
        :size="sizeIco"
        :response-size="responsiveIco ? [16, 16, 16, 20, 20, 20] : []"
        :fill="fill"
        :stroke="stroke || iconColor"
        :hover-fill="hoverFill"
        :hover-stroke="hoverStroke"
      />
    </div>
  </nuxt-link>
  <button
    v-if="mode === 'button'"
    v-bind="$attrs"
    :class="[
      'button-wrapper-link',
      type,
      `${type}__${size}`,
      { button__disabled: disabled },
      { touched: touched },
      { 'button-mode': mode === 'button' },
    ]"
    :style="[
      Number(minWidth) > 0 ? `minWidth: ${minWidth}px` : '',
      resize ? `width: 100% ` : '',
    ]"
    @click="clickButtons"
    @touchstart="touched = true"
  >
    <div v-if="!!iconBefore" class="button__ico-before">
      <UiIcon
        :name="iconBefore"
        :size="sizeIco"
        :response-size="responsiveIco ? [16, 16, 16, 20, 20, 20] : []"
        :fill="fill"
        :stroke="stroke || iconColor"
        :hover-fill="hoverFill"
        :hover-stroke="hoverStroke"
      />
    </div>
    <div :class="['button-text', fontClass]">
      <slot />
    </div>
    <div v-if="!!iconAfter" class="button__ico-after">
      <UiIcon
        :name="iconAfter"
        :size="sizeIco"
        :response-size="responsiveIco ? [16, 16, 16, 20, 20, 20] : []"
        :fill="fill"
        :stroke="stroke || iconColor"
        :hover-fill="hoverFill"
        :hover-stroke="hoverStroke"
      />
    </div>
  </button>
</template>

<style scoped lang="scss" src="./ui-link-css.scss" />
